import React from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CustomSettings,
  isProformaInvoiceTemplateType,
} from "subModule/src/services/invoice/types";
import { useUserLanguages } from "../hooks/useUserLanguages";

interface SummaryFooterSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5
}

const SummaryFooterSettingsPanel: React.FC<SummaryFooterSettingsPanelProps> = ({
  settings,
  onChange,
  templateType = 2,
}) => {
  const { t } = useTranslation();
  const { primaryLanguageName, secondaryLanguageName } = useUserLanguages();

  const updateSummaryItem = (item: string, value: boolean) => {
    onChange({
      summaryItems: {
        ...settings.summaryItems,
        [item]: value,
      },
    });
  };

  const updateFooterItem = (item: string, value: boolean) => {
    onChange({
      footerItems: {
        ...settings.footerItems,
        [item]: value,
      },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Hide summary and payment for inspection type */}
      {templateType !== 5 && (
        <>
          <Box>
            <label
              className="font-Saira font-[600] text-[13px] mb-2 block"
              style={{ color: "#333" }}
            >
              {t("summaryItems")}
            </label>
            <FormGroup sx={{ mt: 1 }}>
              {["subtotal", "discount", "discountedSubtotal", "tax", "total"].map((item) => (
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        settings.summaryItems?.[
                          item as keyof typeof settings.summaryItems
                        ] ?? false
                      }
                      onChange={(e) => updateSummaryItem(item, e.target.checked)}
                    />
                  }
                  label={t(item)}
                  sx={{ m: 0 }}
                />
              ))}
            </FormGroup>
          </Box>

          {!isProformaInvoiceTemplateType(templateType) && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={settings.showPaymentInfo ?? true}
                  onChange={(e) =>
                    onChange({ showPaymentInfo: e.target.checked })
                  }
                />
              }
              label={t("showPaymentInfo")}
              sx={{ m: 0 }}
            />
          )}

          {!isProformaInvoiceTemplateType(templateType) &&
            settings.showPaymentInfo && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            size="small"
            placeholder={(`${t("paymentLabel")} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
            title={(`${t("paymentLabel")} (${primaryLanguageName})`).replace(/\s+/g, ' ').trim()}
            value={settings.paymentLabels?.primary ?? ""}
            onChange={(e) =>
              onChange({
                paymentLabels: {
                  ...settings.paymentLabels,
                  primary: e.target.value,
                },
              })
            }
            fullWidth
          />
          <TextField
            size="small"
            placeholder={(`${t("paymentLabel")} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
            title={(`${t("paymentLabel")} (${secondaryLanguageName})`).replace(/\s+/g, ' ').trim()}
            value={settings.paymentLabels?.secondary ?? ""}
            onChange={(e) =>
              onChange({
                paymentLabels: {
                  ...settings.paymentLabels,
                  secondary: e.target.value,
                },
              })
            }
            fullWidth
          />
        </Box>
          )}
        </>
      )}

      <Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={settings.showFooter ?? true}
              onChange={(e) => onChange({ showFooter: e.target.checked })}
            />
          }
          label={t("showFooter")}
          sx={{ m: 0, mb: 2 }}
        />

        {settings.showFooter && (
          <>
            <Box sx={{ mb: 2 }}>
              <label
                className="font-Saira font-[600] text-[13px] mb-2 block"
                style={{ color: "#333" }}
              >
                {t("footerText")}
              </label>
              <TextField
                size="small"
                value={settings.footerText || ""}
                onChange={(e) => onChange({ footerText: e.target.value })}
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 1 }}
                placeholder={
                  t("footerTextPlaceholder") ||
                  t("This is system generated invoice/JC/WO/Estimation.")
                }
                title={t("footerTextPlaceholder") ||
                  t("This is system generated invoice/JC/WO/Estimation.")}
              />
            </Box>

            <Box>
              <label
                className="font-Saira font-[600] text-[13px] mb-2 block"
                style={{ color: "#333" }}
              >
                {t("footerItems")}
              </label>
              <FormGroup sx={{ mt: 1 }}>
                {[
                  { key: "address", label: t("address") },
                  { key: "email", label: t("email") },
                  { key: "phone", label: t("phone") },
                  { key: "socialLinks", label: t("socialLinks") },
                  // { key: "pageNumber", label: t("pageNumber") },
                ].map((item) => (
                  <FormControlLabel
                    key={item.key}
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          settings.footerItems?.[
                            item.key as keyof typeof settings.footerItems
                          ] ?? false
                        }
                        onChange={(e) =>
                          updateFooterItem(item.key, e.target.checked)
                        }
                      />
                    }
                    label={item.label}
                    sx={{ m: 0 }}
                  />
                ))}
              </FormGroup>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SummaryFooterSettingsPanel;
